/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, Input, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatTableDataSource as MatTableDataSource} from '@angular/material/table';
import {FabConfirmationDialogComponent} from '@fabric/components/confirmation-dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../../common/constants';
import {createColDefs} from '../../../../core/helpers/generic-helpers';
import {ObsoleteRecordsInput} from '../../../../core/models/obsolete-records-input.model';

/**
 * Extendable component to hold common functionality between the remove obsolete records pages
 */
@Component({
    selector: 'app-remove-obsolete-records',
    templateUrl: './remove-obsolete-records.component.html',
    styleUrls: ['./remove-obsolete-records.component.scss'],
    standalone: false
})
export class RemoveObsoleteRecordsComponent implements OnInit {
  @Input() obsoleteRecordsInput: ObsoleteRecordsInput;

  // Boolean to control when table is hidden and if the loading spinner is shown
  noData: boolean;
  showSpinner: boolean;

  dataSource: MatTableDataSource<any> = new MatTableDataSource(null);
  obsoleteRecords: any[] = [];
  displayedColumns: string[] = [];
  columnDefs: ColDef[] = [];
  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };

  constructor(private readonly snackBar: FabMessageService,
              private readonly dialog: MatDialog) {
  }

  ngOnInit(): void {
    this.loadObsoleteRecords();
    this.columnDefs = createColDefs(this.obsoleteRecordsInput.columnHeaders);
  }


  /**
   * Fetches the obsolete records to populate the table
   */
  loadObsoleteRecords(): void {
    this.showSpinner = true;
    this.obsoleteRecordsInput.service.getObsoleteRecords().subscribe(
      (data: any[]) => {
        if (typeof data[0] === 'string') {
          this.obsoleteRecords = data.map(item => ({mktDiv: item}));
        } else {
          this.obsoleteRecords = data;
        }
      },
      err => {
        this.showSpinner = false;
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      () => {
        this.showSpinner = false;
        this.dataSource = new MatTableDataSource(this.obsoleteRecords);
        this.noData = (this.obsoleteRecords.length === 0);
      }
    );
  }

  /**
   * Deletes all obsolete records in the table
   */
  onDelete(): void {
    this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: this.obsoleteRecordsInput.headerMessage,
        message: this.obsoleteRecordsInput.confirmationMessage,
        noLabel: 'Cancel',
        yesLabel: 'Confirm',
        actionColor: 'primary'
      },
      disableClose: true
    }).afterClosed().subscribe(result => {
      if (result) {
        this.showSpinner = true;
        this.obsoleteRecordsInput.service.removeObsoleteRecords().subscribe({
          error: (err: string) => {
            this.loadObsoleteRecords();
            this.snackBar.displayError(err, DISMISS_MSG);
          },
          complete: () => {
            this.loadObsoleteRecords();
            this.snackBar.displaySuccess(this.obsoleteRecordsInput.successMessage, DISMISS_MSG);
          }
        });
      }
    });
  }
}
